// Company Tabs Scroll Page using MUI with Sticky Vertical Subtabs INSIDE Main Content Area
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Fade,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuList,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { styled } from '@mui/system';
import Navbar from '../../components/Navbar';
import {
  BackgroundDiv,
  StyledTab,
  StyledTabs,
} from '../../components/StyledComponents';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AboutUs from './AboutUs';
import ContactUs from './ContactUs';
import { useNavigate, useParams } from 'react-router-dom';
import LegalTerms from './LegalTerms';
import Career from './Career';
import HomePage from './HomePage';
import Pricing from './Pricing';
import Gemstone from './Gemstone';
import WeAreDifferent from './WeAreDifferent';
import MyReferral from './MyReferral';
import Technology from './Technology';
import PrivacyPolicy from './PrivacyPolicy';
import CancellationRefund from './CancellationRefund';
import DeliveryPolicy from './DeliveryPolicy';

const subTabsMap = {
  Home: [],
  Technology: [],
  Pricing: [],
  Company: [
    'About us',
    'Career',
    'Contact',
    'Terms of Service',
    'Privacy Policy',
    'Cancellation and Refunds',
    'Delivery Policy',
  ],
  'Community & Impact': ['We are Different', 'My Referral', 'Gemstone'],
};

const subTabComponentMap = {
  Home: {
    default: <HomePage />,
  },
  Technology: {
    default: <Technology />,
  },
  Pricing: {
    default: <Pricing />,
  },
  Company: {
    'About us': <AboutUs />,
    Career: <Career />,
    Contact: <ContactUs />,
    'Terms of Service': <LegalTerms />,
    'Privacy Policy': <PrivacyPolicy />,
    'Cancellation and Refunds': <CancellationRefund />,
    'Delivery Policy': <DeliveryPolicy />,
  },
  'Community & Impact': {
    'We are Different': <WeAreDifferent />,
    'My Referral': <MyReferral />,
    Gemstone: <Gemstone />,
  },
};

const CompanyTabsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { tab: woSpacetab, subTab: woSpaceSubTab } = useParams();

  const tab = woSpacetab == null ? null : woSpacetab.split('_').join(' ');
  const subTab =
    woSpaceSubTab == null ? null : woSpaceSubTab.split('_').join(' ');
  const activeTab = tab || 'Company';
  const activeSubTab = subTab || subTabsMap[activeTab]?.[0];

  const [showScrollTop, setShowScrollTop] = useState(false);
  const sectionRefs = useRef({});
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    sectionRefs.current = subTabsMap.Company.reduce((acc, label) => {
      acc[label] = acc[label] || React.createRef();
      return acc;
    }, {});
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setShowScrollTop(container.scrollTop > 200);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [subTabOpen, setSubTabOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const handleTabClick = (event, tab) => {
    if (subTabsMap[tab].length) {
      setAnchorEl(event.currentTarget);
      const firstSubTab = subTabsMap[tab][0];
      navigate(
        `/home/${tab.split(' ').join('_')}/${firstSubTab.split(' ').join('_')}`
      );
    } else {
      setAnchorEl(null);
      navigate(`/home/${tab.split(' ').join('_')}`);
    }
  };

  const handleMenuOpen = (label, event) => {
    const { currentTarget } = event;
    if (subTabsMap[label].length > 0) {
      setAnchorEl(currentTarget);
      setSubTabOpen(true);
      setTabValue(label);
    }
  };

  const handleMenuClose = () => {
    console.log('leave');
    setSubTabOpen(false);
    setAnchorEl(null);
  };

  const handleSubTabClick = (label, newTab) => {
    const secondPart = label == null ? null : label.split(' ').join('_');
    const firstPart = newTab == null ? null : newTab.split(' ').join('_');
    handleMenuClose();
    navigate(`/home/${firstPart}/${secondPart}`);
    setAnchorEl(null);
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  return (
    <BackgroundDiv
      sx={{
        height: '100vh',
        display: 'flex',
        overflow: 'hidden',
        flexDirection: 'column',
        [theme.breakpoints.down('sm')]: {
          height: '100dvh',
        },
      }}
      saturn={true}
      isHomePage={true}
    >
      <Navbar
        showSignin={true}
        mobileComponent={
          isMobile ? (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IconButton
                  color="inherit"
                  onClick={handleMobileMenuOpen}
                  sx={{ mt: 0.5 }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>

              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
              >
                {Object.keys(subTabsMap).map((tab) => (
                  <MenuItem
                    key={tab}
                    onClick={(e) => {
                      handleTabClick(e, tab);
                      handleMobileMenuClose();
                    }}
                  >
                    {tab}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : null
        }
      />
      <Box
        sx={{
          px: {
            xs: '20px',
            sm: 'calc((100vw - 760px) / 2)',
            md: 'calc((100vw - 760px) / 2)',
            lg: 'calc((100vw - 760px) / 2)',
            xl: 'calc((100vw - 760px) / 2)',
          },
          overflowY: 'auto',
          alignItems: 'center',
        }}
        ref={scrollContainerRef}
      >
        {!isMobile && (
          <Box
            sx={{
              position: 'sticky',
              top: '0px',
              zIndex: 10,
              bgcolor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              width: '100%',
              height: '50px',
            }}
            onMouseLeave={handleMenuClose}
          >
            <StyledTabs
              value={activeTab}
              textColor="secondary"
              indicatorColor="secondary"
              centered
              sx={{ borderBottom: '1px solid black' }}
            >
              {Object.keys(subTabsMap).map((tab) => (
                <StyledTab
                  key={tab}
                  value={tab}
                  label={tab}
                  onClick={(e) => handleTabClick(e, tab)}
                  onMouseEnter={handleMenuOpen.bind(this, tab)}
                />
              ))}
            </StyledTabs>

            <ClickAwayListener
              onClickAway={handleMenuClose}
              mouseEvent="onMouseDown" // Add this for better compatibility
              touchEvent="onTouchStart" // Add this for mobile support
            >
              <div>
                {' '}
                {/* Wrap in a div to avoid ref conflicts */}
                <TransparentMenu
                  anchorEl={anchorEl}
                  open={subTabOpen}
                  disablePortal
                  onClose={handleMenuClose} // Add explicit onClose handler
                  slotProps={{
                    list: {
                      sx: { padding: 0 },
                    },
                  }}
                  // disable pointer events on menu to enable mouse leave event on parent
                  style={{
                    pointerEvents: 'none',
                  }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <TransparentMenuList
                    style={{
                      pointerEvents: 'auto',
                    }}
                  >
                    {activeTab &&
                      subTabsMap[tabValue]?.map((subTab) => (
                        <MenuItem
                          key={subTab}
                          onClick={() => handleSubTabClick(subTab, tabValue)}
                        >
                          <Typography
                            color="#909090"
                            variant="body2"
                            fontWeight={400}
                          >
                            {subTab}
                          </Typography>
                        </MenuItem>
                      ))}
                  </TransparentMenuList>
                </TransparentMenu>
              </div>
            </ClickAwayListener>
          </Box>
        )}
        {activeTab && (
          <SubTabWrapper>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              {activeSubTab && (
                <Typography
                  variant="h1"
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  {activeSubTab}
                </Typography>
              )}
              <Box
                sx={{
                  top: 180,
                }}
              >
                <SubTabList
                  sx={{
                    marginBottom:
                      subTabsMap[activeTab].length > 0 ? '80px' : '0px',
                  }}
                >
                  {subTabsMap[activeTab].map((label) => (
                    <StyledListItemButton
                      key={label}
                      selected={activeSubTab === label}
                      onClick={() => handleSubTabClick(label, activeTab)}
                      disableGutters
                    >
                      <ListItemText
                        slotProps={{
                          primary: {
                            variant: 'body2',
                            color:
                              activeSubTab === label ? '#454545' : '#909090',
                          },
                        }}
                        primary={label}
                      />
                    </StyledListItemButton>
                  ))}
                </SubTabList>
              </Box>

              <Box sx={{ flex: 1, pr: 1 }}>
                <Box
                  key={activeSubTab}
                  id={activeSubTab}
                  ref={sectionRefs.current[activeSubTab]}
                  sx={{ scrollMarginTop: '400px' }}
                >
                  <Fade in={true} timeout={500}>
                    <Box>
                      {Object.values(subTabComponentMap[activeTab]).length > 1
                        ? subTabComponentMap[activeTab][activeSubTab]
                        : subTabComponentMap[activeTab].default}
                    </Box>
                  </Fade>
                </Box>
              </Box>
            </Box>
          </SubTabWrapper>
        )}

        <Fade in={showScrollTop}>
          <IconButton
            onClick={() => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
              }
            }}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              bgcolor: 'black',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <ArrowUpwardIcon />
          </IconButton>
        </Fade>
      </Box>
    </BackgroundDiv>
  );
};

const SubTabWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
}));

const SubTabList = styled(List)(({ theme }) => ({
  background: 'tranparent',
  minWidth: 180,
  padding: 0,
  [theme.breakpoints.down('md')]: {
    minWidth: 120,
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 100,
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  minHeight: 32,
  backgroundColor: 'transparent',
  '&.Mui-selected': {
    backgroundColor: 'transparent',
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'transparent',
  },
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&:active': {
    backgroundColor: 'transparent',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'transparent',
  },
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
}));

const TransparentMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.background.default,
    boxShadow: 'none',
    marginTop: theme.spacing(0.5),
  },
}));

const TransparentMenuList = styled(MenuList)(({ theme }) => ({
  padding: 0,
  backgroundColor: 'transparent',
  '&:focus-visible': {
    outline: 'none',
  },
  '& .MuiMenuItem-root': {
    backgroundColor: 'transparent',
    color: '#909090',
    fontSize: '18px',
    padding: theme.spacing(1.2, 3),
    '&:hover': {
      // backgroundColor: '#b0b0b0',
      color: '#454545',
    },
    '&.Mui-selected': {
      backgroundColor: 'transparent',
      color: '#454545',
    },
  },
}));

export const YellowBoxBottom = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(135, 65, 0, 0.2)' : '#fffaf5',
  borderRadius: 2,
  textAlign: 'center',
  padding: '20px 30px',
  marginTop: '20 px',
  marginBottom: '100px',
  [theme.breakpoints.up('sm')]: {
    marginTop: '50px',
    marginBottom: '100px',
  },
  [theme.breakpoints.up('md')]: {
    marginTop: '100px',
    marginBottom: '100px',
  },
  [theme.breakpoints.up('lg')]: {
    marginTop: '100px',
    marginBottom: '100px',
  },
}));

export default CompanyTabsPage;
